'use client'

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { Loader2, Plus, Pencil, Trash, Share2, Upload, ImageIcon, Edit, X, ChevronRight } from 'lucide-react';
import axios from 'axios';
import './style/AiGeneratedExam.css';

export default function AiGeneratedExam() {
  const [exam, setExam] = useState({
    _id: '',
    exam_title: '',
    description: '',
    duration: 0,
    start_time: new Date(),
    end_time: new Date(),
    class: '',
    subject: '',
    createdBy: '',
    questions: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editedQuestion, setEditedQuestion] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    type: ['Mcq'],
    question_text: '',
    marks: 0,
    options: ['', '', '', ''],
    correct_answer: ''
  });
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchExam = async () => {
      console.log('Stored exam data:', localStorage.getItem('examData'));
      console.log('Location state:', location.state);
      setLoading(true);
      try {
        const storedExamData = localStorage.getItem('examData');
        if (storedExamData) {
          const parsedData = JSON.parse(storedExamData);
          if (parsedData && parsedData.questions && parsedData.questions.length > 0) {
            const questionData = JSON.parse(parsedData.questions[0].question_text);
            setExam({
              ...parsedData,
              questions: questionData.exam.questions
            });
          } else {
            throw new Error('Invalid exam data structure');
          }
        } else if (location.state && location.state.exam) {
          const examData = location.state.exam;
          if (examData.questions && examData.questions.length > 0) {
            const questionData = JSON.parse(examData.questions[0].question_text);
            setExam({
              ...examData,
              questions: questionData.exam.questions
            });
          } else {
            throw new Error('Invalid data structure in API response');
          }
        } else {
          throw new Error('No exam data found');
        }
      } catch (err) {
        console.error('Error fetching exam data:', err);
        setError(err.message || 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchExam();
  }, [location.state]);

  const renderMathText = (text) => {
    const parts = text.split(/(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/);
    return parts.map((part, i) => {
      if (part.startsWith('$$') && part.endsWith('$$')) {
        return <BlockMath key={i}>{part.slice(2, -2)}</BlockMath>;
      } else if (part.startsWith('$') && part.endsWith('$')) {
        return <InlineMath key={i}>{part.slice(1, -1)}</InlineMath>;
      } else {
        return <span key={i}>{part}</span>;
      }
    });
  };

  const handleDelete = (index) => {
    setExam(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setEditedQuestion(exam.questions[index]);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (editingIndex !== -1) {
      setExam(prev => ({
        ...prev,
        questions: prev.questions.map((q, i) => i === editingIndex ? editedQuestion : q)
      }));
    }
    setIsEditModalOpen(false);
  };

  const handleAddQuestion = () => {
    setIsAddModalOpen(true);
  };

  const handleSaveNewQuestion = () => {
    setExam(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
    setIsAddModalOpen(false);
    setNewQuestion({
      type: ['Mcq'],
      question_text: '',
      marks: 0,
      options: ['', '', '', ''],
      correct_answer: ''
    });
  };

  const createUpdatedExamObject = () => {
    return {
      ...exam,
      questions: [
        {
          question_text: JSON.stringify({
            exam: {
              questions: exam.questions
            }
          }),
          mcq_options: []
        }
      ]
    };
  };

  const handleShareExam = async () => {
    setIsShareModalOpen(false);
    setLoading(true);
    const updatedExam = createUpdatedExamObject();
    console.log('Updated Exam Object:', updatedExam);
    
    try {
      const response = await axios.put('http://localhost:5000/api/exam/update-exam', updatedExam);
      console.log('Server response:', response.data);
      
      if (response.status === 200) {
        localStorage.removeItem('examData');
        setTimeout(() => {
          setLoading(false);
          navigate('/exams/new');
        }, 2000);
      } else {
        throw new Error('Failed to update exam');
      }
    } catch (error) {
      console.error('Error updating exam:', error);
      setLoading(false);
      setError('Failed to share exam. Please try again.');
    }
  };

  const handleUploadAnswerKey = () => {
    setIsUploadModalOpen(true);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleSaveUploadedFile = async () => {
    if (uploadedFile) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('examName', exam.exam_title);

      try {
        const response = await axios.post('http://localhost:5000/api/exam/answerKeyUpload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.status === 200) {
          alert('Answer key uploaded successfully!');
          setIsUploadModalOpen(false);
          setUploadedFile(null);
        } else {
          throw new Error('Failed to upload answer key');
        }
      } catch (error) {
        console.error('Error uploading answer key:', error);
        alert('Error uploading answer key');
      } finally {
        setIsUploading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <Loader2 className="animate-spin h-16 w-16 text-blue-500 mx-auto" />
          <p className="text-center mt-4">Loading exam data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>;
  }

  return (
    <div className='bg-gradient-to-br from-blue-100 to-purple-100 min-h-screen'>
      <div className="unique17-container">
        <h1 className="unique17-title">{exam.exam_title || 'AI-Generated Exam'}</h1>
        
        <div className="unique17-button-group">
          <button onClick={handleAddQuestion} className="unique17-add-button">
            <Plus className="unique17-button-icon" /> Add Question
          </button>
          <button onClick={() => setIsShareModalOpen(true)} className="unique17-share-button">
            <Share2 className="unique17-button-icon" /> Share Exam
          </button>
          <button onClick={handleUploadAnswerKey} className="unique17-upload-button">
            <Upload className="unique17-button-icon" /> Upload Answer Key
          </button>
        </div>

        <div className="unique17-questions-list">
          {exam.questions.map((question, index) => (
            <div key={index} className="unique17-question-card">
              <div className="unique17-question-header">
                <h2 className="unique17-question-number">Question {index + 1}</h2>
                <div className="unique17-question-actions">
                  <button className="unique17-edit-button" onClick={() => handleEdit(index)}>
                    <Edit />
                  </button>
                  <button className="unique17-delete-button" onClick={() => handleDelete(index)}>
                    <Trash />
                  </button>
                </div>
              </div>
              <div className="unique17-question-content">
                <div className="unique17-question-text">
                  {renderMathText(question.question_text)}
                </div>
                {question.type[0] === 'Mcq' && (
                  <div className="unique17-mcq-section">
                    <h3 className="unique17-mcq-title">Options:</h3>
                    <ul className="unique17-mcq-options">
                      {question.options.map((option, i) => (
                        <li key={i} className="unique17-mcq-option">
                          <span className="unique17-mcq-option-letter">{String.fromCharCode(97 + i)})</span>
                          <span className="unique17-mcq-option-text">{renderMathText(option)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="unique17-correct-answer">
                      <strong>Correct Answer:</strong> {renderMathText(question.correct_answer)}
                    </div>
                  </div>
                )}
                <div className="unique17-question-marks">
                  Marks: {question.marks}
                </div>
              </div>
            </div>
          ))}
        </div>

        {isEditModalOpen && (
          <div className="unique17-modal-overlay">
            <div className="unique17-modal-content">
              <h2 className="unique17-modal-title">Edit Question</h2>
              <select
                value={editedQuestion.type[0]}
                onChange={(e) => setEditedQuestion({...editedQuestion, type: [e.target.value]})}
                className="unique17-modal-select"
              >
                <option value="Mcq">Multiple Choice</option>
                <option value="Essay">Essay Type</option>
              </select>
              <textarea
                value={editedQuestion.question_text}
                onChange={(e) => setEditedQuestion({...editedQuestion, question_text: e.target.value})}
                className="unique17-modal-textarea"
                placeholder="Enter question text"
              />
              <input
                type="number"
                value={editedQuestion.marks || 0}
                onChange={(e) => setEditedQuestion({...editedQuestion, marks: parseInt(e.target.value, 10)})}
                className="unique17-modal-input"
                placeholder="Marks"
                min="0"
              />
              {editedQuestion.type[0] === 'Mcq' && (
                <div className="unique17-modal-mcq-options">
                  {(editedQuestion.options || []).map((option, index) => (
                    <div key={index} className="unique17-modal-mcq-option-container">
                      <span className="unique17-modal-mcq-option-letter">{String.fromCharCode(97 + index)})</span>
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...editedQuestion.options];
                          newOptions[index] = e.target.value;
                          setEditedQuestion({...editedQuestion, options: newOptions});
                        }}
                        className="unique17-modal-mcq-option"
                        placeholder={`Option ${index + 1}`}
                      />
                    </div>
                  ))}
                  <input
                    type="text"
                    value={editedQuestion.correct_answer || ''}
                    onChange={(e) => setEditedQuestion({...editedQuestion, correct_answer: e.target.value})}
                    placeholder="Correct Answer"
                    className="unique17-modal-input mt-2"
                  />
                </div>
              )}
              <div className="unique17-modal-actions">
                <button onClick={handleSaveEdit} className="unique17-modal-save-button">
                  <Pencil className="unique17-button-icon" /> Save Changes
                </button>
                <button onClick={() => setIsEditModalOpen(false)} className="unique17-modal-cancel-button">
                  <X className="unique17-button-icon" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {isAddModalOpen && (
          <div className="unique17-modal-overlay">
            <div className="unique17-modal-content">
              <h2 className="unique17-modal-title">Add New Question</h2>
              <select
                value={newQuestion.type[0]}
                onChange={(e) => setNewQuestion({...newQuestion, type: [e.target.value]})}
                className="unique17-modal-select"
              >
                <option value="Mcq">Multiple Choice</option>
                <option value="Essay">Essay Type</option>
              </select>
              <textarea
                value={newQuestion.question_text}
                onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})}
                placeholder="Enter your question here"
                className="unique17-modal-textarea"
              />
              <input
                type="number"
                value={newQuestion.marks}
                onChange={(e) => setNewQuestion({...newQuestion, marks: parseInt(e.target.value, 10)})}
                className="unique17-modal-input"
                placeholder="Marks"
                min="0"
              />
              {newQuestion.type[0] === 'Mcq' && (
                <div className="unique17-modal-mcq-options">
                  {newQuestion.options.map((option, index) => (
                    <div key={index} className="unique17-modal-mcq-option-container">
                      <span className="unique17-modal-mcq-option-letter">{String.fromCharCode(97 + index)})</span>
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...newQuestion.options];
                          newOptions[index] = e.target.value;
                          setNewQuestion({...newQuestion, options: newOptions});
                        }}
                        className="unique17-modal-mcq-option"
                        placeholder={`Option ${index + 1}`}
                      />
                    </div>
                  ))}
                  <input
                    type="text"
                    value={newQuestion.correct_answer}
                    onChange={(e) => setNewQuestion({...newQuestion, correct_answer: e.target.value})}
                    placeholder="Correct Answer"
                    className="unique17-modal-input mt-2"
                  />
                </div>
              )}
              <div className="unique17-modal-actions">
                <button onClick={handleSaveNewQuestion} className="unique17-modal-save-button">
                  <Plus className="unique17-button-icon" /> Add Question
                </button>
                <button onClick={() => setIsAddModalOpen(false)} className="unique17-modal-cancel-button">
                  <X className="unique17-button-icon" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {isShareModalOpen && (
          <div className="unique17-modal-overlay">
            <div className="unique17-modal-content">
              <h2 className="unique17-modal-title">Share Exam</h2>
              <p className="unique17-modal-text">Are you sure you want to share this exam with students?</p>
              <div className="unique17-modal-actions">
                <button onClick={() => setIsShareModalOpen(false)} className="unique17-modal-cancel-button">
                  <X className="unique17-button-icon" /> Cancel
                </button>
                <button onClick={handleShareExam} className="unique17-modal-share-button">
                  <Share2 className="unique17-button-icon" /> Confirm Share
                </button>
              </div>
            </div>
          </div>
        )}

        {isUploadModalOpen && (
          <div className="unique17-modal-overlay">
            <div className="unique17-modal-content">
              <h2 className="unique17-modal-title">Upload Answer Key</h2>
              <input 
                type="file" 
                onChange={handleFileUpload}
                accept=".doc,.docx,.pdf,.xls,.xlsx"
                className="unique17-modal-file-input"
              />
              {uploadedFile && (
                <p className="unique17-modal-file-name">Selected file: {uploadedFile.name}</p>
              )}
              <div className="unique17-modal-actions">
                <button onClick={() => setIsUploadModalOpen(false)} className="unique17-modal-cancel-button">
                  <X className="unique17-button-icon" /> Cancel
                </button>
                <button
                  onClick={handleSaveUploadedFile}
                  disabled={!uploadedFile || isUploading}
                  className="unique17-modal-upload-button"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="unique17-button-icon animate-spin" /> Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="unique17-button-icon" /> Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="unique17-loading-overlay">
            <div className="unique17-loading-content">
              <Loader2 className="unique17-loading-icon animate-spin" />
              <p className="unique17-loading-text">Processing...</p>
            </div>
          </div>
        )}

        <button className="unique17-nav-button" onClick={() => navigate('/exams/exam-summary')}>
          <ChevronRight className="unique17-button-icon" /> View Exam Summary
        </button>
      </div>
    </div>
  );
}

